<template>
    <draggable
        :list="localNodes"
        item-key="id"
        tag="ul"
        class="corp-tree"
        handle=".corp-tree__drag-handle"
        ghost-class="corp-tree__ghost"
        :disabled="!canManage"
        @end="onDragEnd"
    >
        <template #item="{ element: node }">
            <li class="corp-tree__item">
                <div
                    class="corp-tree__node"
                    :class="[
                        `corp-tree__node--${node.type}`,
                        { 'corp-tree__node--inactive': node.status !== 'active' },
                    ]"
                >
                    <button
                        v-if="canManage && node.type !== 'company'"
                        type="button"
                        class="corp-tree__drag-handle"
                        :title="labels.move"
                        :aria-label="labels.move"
                    >
                        <v-icon size="small">mdi-drag-vertical</v-icon>
                    </button>
                    <span v-else class="corp-tree__drag-handle corp-tree__drag-handle--spacer" />

                    <button
                        v-if="hasChildren(node)"
                        type="button"
                        class="corp-tree__toggle"
                        @click="toggle(node.id)"
                        :aria-label="isExpanded(node.id) ? t.structure.collapse : t.structure.expand"
                    >
                        <fa :icon="isExpanded(node.id) ? 'fa-chevron-down' : 'fa-chevron-right'" />
                    </button>
                    <span v-else class="corp-tree__toggle corp-tree__toggle--spacer" />

                    <div class="corp-tree__icon" aria-hidden="true">
                        <fa :icon="typeIcon(node.type)" />
                    </div>

                    <div class="corp-tree__main">
                        <div class="corp-tree__title-row">
                            <button
                                v-if="isDetailNode(node)"
                                type="button"
                                class="corp-tree__name corp-tree__name--link"
                                @click="$emit('open', node)"
                            >
                                {{ node.name }}
                            </button>
                            <strong v-else class="corp-tree__name">{{ node.name }}</strong>
                            <span class="corp-tree__badge" :class="`corp-tree__badge--${node.type}`">
                                {{ typeLabel(node.type) }}
                            </span>
                            <span
                                class="corp-tree__status"
                                :class="node.status === 'active' ? 'corp-tree__status--on' : 'corp-tree__status--off'"
                            >
                                {{ node.status === 'active' ? labels.active : labels.inactive }}
                            </span>
                        </div>
                        <div class="corp-tree__meta">
                            <span>
                                <fa icon="fa-users" />
                                {{ node.usersCount || 0 }} {{ labels.users }}
                            </span>
                            <span>
                                <fa icon="fa-book" />
                                {{ node.coursesCount || 0 }} {{ labels.courses }}
                            </span>
                            <span v-if="showManager && node.managerName">
                                <fa icon="fa-user-tie" />
                                {{ node.managerName }}
                            </span>
                        </div>
                    </div>

                    <div class="corp-tree__actions" v-if="canManage || isDetailNode(node)">
                        <template v-if="canManage">
                            <v-btn
                                v-if="canAddChild(node)"
                                icon
                                size="small"
                                variant="tonal"
                                color="primary"
                                rounded="lg"
                                :title="addChildLabel(node)"
                                :aria-label="addChildLabel(node)"
                                @click="onAddChild(node)"
                            >
                                <v-icon size="small">mdi-plus</v-icon>
                            </v-btn>

                            <v-btn
                                icon
                                size="small"
                                variant="text"
                                :title="labels.edit"
                                :aria-label="labels.edit"
                                @click="$emit('edit', node)"
                            >
                                <v-icon size="small">mdi-pencil</v-icon>
                            </v-btn>
                        </template>

                        <v-btn
                            v-if="isDetailNode(node)"
                            icon
                            size="small"
                            variant="text"
                            color="primary"
                            :title="labels.openDetail || t.structure.openDetail"
                            :aria-label="labels.openDetail || t.structure.openDetail"
                            @click="$emit('open', node)"
                        >
                            <v-icon size="small">mdi-chart-bar</v-icon>
                        </v-btn>

                        <template v-if="canManage">
                            <v-btn
                                v-if="node.type !== 'company'"
                                icon
                                size="small"
                                variant="text"
                                color="error"
                                :title="labels.delete"
                                :aria-label="labels.delete"
                                @click="$emit('remove', node)"
                            >
                                <v-icon size="small">mdi-delete-outline</v-icon>
                            </v-btn>
                        </template>
                    </div>
                </div>

                <OrgTree
                    v-if="hasChildren(node) && isExpanded(node.id)"
                    :nodes="node.children"
                    :parent-id="node.id"
                    :labels="labels"
                    :can-manage="canManage"
                    :show-manager="showManager"
                    :expanded-ids="expandedIds"
                    @edit="$emit('edit', $event)"
                    @remove="$emit('remove', $event)"
                    @toggle-status="$emit('toggle-status', $event)"
                    @add-child="$emit('add-child', $event)"
                    @toggle-expand="$emit('toggle-expand', $event)"
                    @reorder="$emit('reorder', $event)"
                    @open="$emit('open', $event)"
                />
            </li>
        </template>
    </draggable>
</template>

<script>
import draggable from 'vuedraggable';
import { corporateI18n } from '@/i18n/mixins';

export default {
    name: 'OrgTree',
    mixins: [corporateI18n],
    components: { draggable },
    props: {
        nodes: {
            type: Array,
            default: () => [],
        },
        parentId: {
            type: [String, Number],
            default: null,
        },
        labels: {
            type: Object,
            required: true,
        },
        canManage: {
            type: Boolean,
            default: false,
        },
        showManager: {
            type: Boolean,
            default: true,
        },
        expandedIds: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['edit', 'remove', 'toggle-status', 'add-child', 'toggle-expand', 'reorder', 'open'],
    data() {
        return {
            localNodes: [],
            orderSnapshot: '',
        };
    },
    watch: {
        nodes: {
            immediate: true,
            deep: true,
            handler(value) {
                this.localNodes = Array.isArray(value) ? [...value] : [];
                this.orderSnapshot = this.orderSignature(this.localNodes);
            },
        },
    },
    methods: {
        orderSignature(nodes = []) {
            return (nodes || []).map((node) => String(node.id)).join('|');
        },
        typeLabel(type) {
            return this.labels.types?.[type] || type;
        },
        isDetailNode(node) {
            return node?.type === 'sector' || node?.type === 'role';
        },
        typeIcon(type) {
            if (type === 'company') return 'fa-building';
            if (type === 'board') return 'fa-landmark';
            if (type === 'sector') return 'fa-diagram-project';
            if (type === 'role') return 'fa-id-badge';
            return 'fa-circle';
        },
        hasChildren(node) {
            return Boolean(node.children && node.children.length);
        },
        isExpanded(id) {
            return this.expandedIds[id] !== false;
        },
        toggle(id) {
            this.$emit('toggle-expand', id);
        },
        canAddChild(node) {
            return node.type === 'company' || node.type === 'board' || node.type === 'sector';
        },
        childType(node) {
            if (node.type === 'company' || node.type === 'board') return 'sector';
            if (node.type === 'sector') return 'role';
            return null;
        },
        addChildLabel(node) {
            const type = this.childType(node);
            if (type === 'sector') return this.labels.addSector;
            if (type === 'role') return this.labels.addRole;
            return this.labels.addChild;
        },
        onAddChild(node) {
            const type = this.childType(node);
            if (!type) return;
            this.$emit('add-child', { parent: node, type });
        },
        onDragEnd() {
            const nextSignature = this.orderSignature(this.localNodes);
            if (nextSignature === this.orderSnapshot) return;
            this.$emit('reorder', {
                parentId: this.parentId,
                orderedIds: this.localNodes.map((node) => node.id),
            });
        },
    },
};
</script>

<style scoped>
.corp-tree {
    list-style: none;
    margin: 0;
    padding: 0;
}

.corp-tree__item {
    margin: 0;
}

.corp-tree__item :deep(> .corp-tree) {
    list-style: none;
    margin: 0.35rem 0 0.35rem 1.35rem;
    padding: 0 0 0 0.85rem;
    border-left: 1px solid #e8eaf0;
}

.corp-tree__node {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.8rem 0.9rem;
    margin-bottom: 0.45rem;
    border-radius: 12px;
    border: 1px solid #e8eaf0;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    flex-wrap: wrap;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.corp-tree__node:hover {
    border-color: rgba(37, 8, 145, 0.28);
    box-shadow: 0 4px 12px rgba(37, 8, 145, 0.06);
}

.corp-tree__node--role {
    background: linear-gradient(135deg, rgba(26, 199, 208, 0.06) 0%, #fff 55%);
}

.corp-tree__node--board {
    background: linear-gradient(135deg, rgba(37, 8, 145, 0.04) 0%, #fff 55%);
}

.corp-tree__node--inactive {
    opacity: 0.72;
}

.corp-tree__ghost {
    opacity: 0.55;
}

.corp-tree__drag-handle {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #8b929e;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    flex-shrink: 0;
    padding: 0;
}

.corp-tree__drag-handle:active {
    cursor: grabbing;
}

.corp-tree__drag-handle--spacer {
    visibility: hidden;
    pointer-events: none;
    cursor: default;
}

.corp-tree__toggle {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: #f4f5f8;
    color: #5c6370;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.corp-tree__toggle--spacer {
    visibility: hidden;
    pointer-events: none;
}

.corp-tree__icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #eef0fc;
    color: #250891;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.corp-tree__node--role .corp-tree__icon {
    background: rgba(26, 199, 208, 0.15);
    color: #0e8f96;
}

.corp-tree__node--sector .corp-tree__icon {
    background: rgba(57, 36, 151, 0.1);
    color: #392497;
}

.corp-tree__main {
    flex: 1;
    min-width: 180px;
    text-align: left;
}

.corp-tree__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 0.55rem;
}

.corp-tree__name {
    font-size: 0.95rem;
    color: #1a1d26;
}

.corp-tree__name--link {
    border: 0;
    background: transparent;
    padding: 0;
    font: inherit;
    font-weight: 700;
    color: #250891;
    cursor: pointer;
    text-align: left;
}

.corp-tree__name--link:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
}

.corp-tree__badge {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.2rem 0.45rem;
    border-radius: 999px;
    background: #eef0fc;
    color: #250891;
}

.corp-tree__badge--role {
    background: rgba(26, 199, 208, 0.16);
    color: #0e8f96;
}

.corp-tree__badge--sector {
    background: rgba(57, 36, 151, 0.12);
    color: #392497;
}

.corp-tree__badge--board {
    background: #eef0fc;
    color: #250891;
}

.corp-tree__status {
    font-size: 0.68rem;
    font-weight: 600;
    padding: 0.15rem 0.4rem;
    border-radius: 999px;
}

.corp-tree__status--on {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
}

.corp-tree__status--off {
    background: rgba(198, 40, 40, 0.1);
    color: #c62828;
}

.corp-tree__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem 0.9rem;
    margin-top: 0.3rem;
    font-size: 0.75rem;
    color: #5c6370;
}

.corp-tree__meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
}

.corp-tree__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.1rem;
    align-items: center;
    margin-left: auto;
}

@media (max-width: 900px) {
    .corp-tree__actions {
        width: 100%;
        margin-left: 0;
        padding-top: 0.35rem;
        border-top: 1px solid #f0f2f5;
    }

    .corp-tree__item :deep(> .corp-tree) {
        margin-left: 0.5rem;
        padding-left: 0.5rem;
    }
}
</style>
